import { useLocalStorage } from "@/hooks/useLocalStorage";
import { ROUTES } from "@/routes_new";
import { createContext, ReactNode, useContext, useState } from "react";
import { useParams } from "react-router-dom";

type SignupContextType = {
  email?: string;
  companyName?: string;
  fullCompanyName?: string;
  websiteUrl?: string;
  companyDescription?: string;
  useCases?: string[];
  customUseCase?: string;
  setEmail: (email: string) => void;
  setCompanyName: (companyName: string) => void;
  setFullCompanyName: (fullCompanyName?: string) => void;
  setWebsiteUrl: (websiteUrl?: string) => void;
  setCompanyDescription: (companyDescription?: string) => void;
  setUseCases: (useCases?: string[]) => void;
  setCustomUseCase: (customUseCase?: string) => void;
};

const SignupContext = createContext<SignupContextType>({
  email: undefined,
  companyName: undefined,
  fullCompanyName: undefined,
  websiteUrl: undefined,
  companyDescription: undefined,
  useCases: undefined,
  customUseCase: undefined,
  setEmail: () => undefined,
  setCompanyName: () => undefined,
  setFullCompanyName: () => undefined,
  setWebsiteUrl: () => undefined,
  setCompanyDescription: () => undefined,
  setUseCases: () => undefined,
  setCustomUseCase: () => undefined,
});

export function SignupProvider({ children }: { children: ReactNode }) {
  // Local storage state
  const [companyName, setCompanyName] = useLocalStorage<string | undefined>(
    "onboarding/company-name",
    undefined
  );
  const [fullCompanyName, setFullCompanyName] = useLocalStorage<
    string | undefined
  >("onboarding/full-company-name", undefined);
  const [companyDescription, setCompanyDescription] = useLocalStorage<
    string | undefined
  >("onboarding/company-description", undefined);
  const [websiteUrl, setWebsiteUrl] = useLocalStorage<string | undefined>(
    "onboarding/website-url",
    undefined
  );
  const [useCases, setUseCases] = useLocalStorage<string[] | undefined>(
    "onboarding/use-cases",
    []
  );
  const [customUseCase, setCustomUseCase] = useLocalStorage<string | undefined>(
    "onboarding/custom-use-case",
    undefined
  );
  const [email, setEmail] = useState<string>();

  return (
    <SignupContext.Provider
      value={{
        email,
        companyName,
        fullCompanyName,
        websiteUrl,
        companyDescription,
        useCases,
        customUseCase,
        setEmail,
        setCompanyName,
        setFullCompanyName,
        setWebsiteUrl,
        setCompanyDescription,
        setUseCases,
        setCustomUseCase,
      }}
    >
      {children}
    </SignupContext.Provider>
  );
}

export function useSignupContext() {
  return useContext(SignupContext);
}

export function useSignupRoutes() {
  const { language } = useParams<{ language: string }>();
  return {
    SIGNUP: ROUTES.SIGNUP_LOCALIZED.replace(":language", language),
    SIGNUP_INFO: ROUTES.SIGNUP_INFO_LOCALIZED.replace(":language", language),
    SIGNUP_CONFIRM: ROUTES.SIGNUP_CONFIRM_LOCALIZED.replace(
      ":language",
      language
    ),
  };
}
